import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import { Form, Formik } from 'formik';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import useNoChangeWarning from '@acorn-portal/shared/src/hooks/useNoChangeWarning';
import useGetOrCreateMidTermAdjustment, {
	handleChangeOfUsageOrMilageRating,
} from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { useDiscardMta } from '@acorn-portal/shared/src/hooks/use-discard-mta';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import { updateMidTermAdjustmentDetails } from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { setMidTermAdjustmentRatingResponse } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import { MtaChangeType } from '@acorn-portal/shared/src/services/api';

import { COVER_LEVEL_UPDATED, TAXI_VEHICLE_USE, VEHICLE_USED_FOR } from '@acorn-portal/shared/src/utils/modalText';
import getClosestMileage from '@acorn-portal/shared/src/utils/getClosestMileage';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import {
	TAXI_ERRORS,
	VEHICLE_DETAILS_PART_TWO_ERROR_MESSAGES,
} from '@acorn-portal/shared/src/utils/constants/ErrorMessages';

import * as Yup from 'yup';
import AutoCompleteFormField from '@acorn-portal/shared/src/components/inputs/AutoCompleteFormField';
import { checkFormConditions } from '@acorn-portal/shared/src/pages/mta/utils';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

export interface IChangeUsageOrMileageForm {
	vehicleUse: string;
	levelOfCover: string;
	annualMileage: string;
	businessMileage?: string;
	licencingAuthority?: string;
	operatingArea?: string;
}

const ChangeOfUsageTaxi = () => {
	const [open, toggleModal] = useState(false);
	const [loading, setLoading] = useState(false);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const mtaChanges = useAppSelector((redux) => redux.midTermAdjustment);
	const [hasChanged] = useNoChangeWarning();
	const activePolicyDetails = useAppSelector((redux) => redux.midTermAdjustmentAPI);
	const lists = useAppSelector((redux) => redux.referenceData);
	const { track } = useTrack();

	const { midTermAdjustment } = useGetOrCreateMidTermAdjustment({
		changeType: MtaChangeType.CHANGE_USAGE_AND_MILEAGE,
		setAdjustmentLoading: setLoading,
	});

	const mtaResponse = useAppSelector((redux) => redux.midTermAdjustmentResponse);

	useEffect(() => {
		if (!mtaChanges.policyReference) {
			navigate(ROUTE_PATHS.home);
		}
	}, []);

	useEffect(() => {
		track(`Change of Usage Taxi Page Viewed`);
	}, []);

	const isTaxi = fullPolicy.product === Product.TAXI;

	const { discardLoading } = useDiscardMta();

	const initialValues = {
		vehicleUse: midTermAdjustment.vehicleDetails.vehicleUse,
		levelOfCover: midTermAdjustment.vehicleDetails.coverDefinitionCode ?? '',
		annualMileage: getClosestMileage(
			lists.millageIncrementOptions as IOptions[],
			midTermAdjustment.vehicleDetails.annualMileage,
		),
		businessMileage: getClosestMileage(
			lists.millageIncrementOptions as IOptions[],
			midTermAdjustment.vehicleDetails.businessMileage,
		),
		licencingAuthority: midTermAdjustment.vehicleDetails.licencingAuthority ?? '',
		operatingArea: midTermAdjustment.policyDetails.operatingArea ?? fullPolicy.coverDetails.operatingArea,
	};

	const taxiValidationSchema = Yup.object({
		vehicleUse: Yup.string().required(VEHICLE_DETAILS_PART_TWO_ERROR_MESSAGES.vehicleUse),
		licencingAuthority: Yup.string().required(TAXI_ERRORS.licensingAuthority),
		operatingArea: Yup.string().required(TAXI_ERRORS.operatingArea),
	});

	const onSubmit = async (values: IChangeUsageOrMileageForm): Promise<void> => {
		const newMTAChanges = values;
		const activePolicyValues = {
			vehicleUse: activePolicyDetails.vehicleDetails.vehicleUse,
			levelOfCover: activePolicyDetails.vehicleDetails.coverDefinitionCode ?? '',
			annualMileage: getClosestMileage(
				lists.millageIncrementOptions as IOptions[],
				activePolicyDetails.vehicleDetails.annualMileage,
			),
			businessMileage: getClosestMileage(
				lists.millageIncrementOptions as IOptions[],
				activePolicyDetails.vehicleDetails.businessMileage,
			),
			licencingAuthority: activePolicyDetails.vehicleDetails.licencingAuthority,
			operatingArea: activePolicyDetails.policyDetails.operatingArea,
		};

		const { updateSection, continueToRate, navigateToSummary } = checkFormConditions(
			hasChanged(activePolicyValues, newMTAChanges),
			hasChanged(initialValues, newMTAChanges),
			mtaResponse,
			mtaChanges.changeOfUsage,
		);

		if (!continueToRate && !navigateToSummary) {
			return;
		}
		if (!continueToRate && navigateToSummary) {
			navigate(ROUTE_PATHS.makeAChangeSummary);
			return;
		}
		setLoading(true);
		try {
			const result = await handleChangeOfUsageOrMilageRating({
				midTermAdjustment,
				values,
				product: fullPolicy.product,
			});

			if (!result) throw new Error('No result from handleChangeOfUsageOrMilageRating');

			dispatch(
				updateMidTermAdjustmentDetails({
					...midTermAdjustment,
					changeOfUsage: updateSection,
					vehicleDetails: {
						...midTermAdjustment.vehicleDetails,
						annualMileage: values.annualMileage ? parseInt(values.annualMileage, 10) : 0,
						businessMileage: values.businessMileage ? parseInt(values.businessMileage.toString(), 10) : 0,
						coverDefinitionCode: values.levelOfCover,
						vehicleUse: values.vehicleUse,
						licencingAuthority: values.licencingAuthority,
					},
					policyDetails: {
						...midTermAdjustment.policyDetails,
						operatingArea: values.operatingArea ?? '',
					},
				}),
			);
			if (result?.hasDeclined) {
				navigate(ROUTE_PATHS.makeAChangeReferral);
				return;
			}
			dispatch(setMidTermAdjustmentRatingResponse(result));
			setLoading(false);
			navigate(ROUTE_PATHS.makeAChangeSummary);
		} catch (error) {
			console.error('Error in Change of Usage:', error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	const coverOptions =
		activePolicyDetails.vehicleDetails.coverDefinitionCode === 'COMP'
			? (lists.levelOfCoverTypes as IOptions[])
			: (lists.levelOfCoverTypes.filter((x) => x.value !== 'COMP') as IOptions[]);

	const taxiUseOptionsFilter = (lists.vehicleUseOptions as IOptions[]).filter((x) => x.value !== '4');

	return (
		<JourneyCardLayout pageTitle='Change of Usage' loading={loading || discardLoading}>
			<Formik<IChangeUsageOrMileageForm>
				key={JSON.stringify(midTermAdjustment.transactionId)}
				validationSchema={taxiValidationSchema}
				initialValues={initialValues}
				onSubmit={onSubmit}
				onReset={() => navigate(-1)}
			>
				{(formik) => (
					<Form>
						<FormikLogErrors />
						{!isTaxi ? (
							<ToggleButtonFormField
								label='What will you be using the vehicle for?'
								name='vehicleUse'
								id='vehicleUse'
								value={formik.values.vehicleUse}
								onChange={(value) => {
									formik.setFieldValue('vehicleUse', value);
								}}
								options={lists.vehicleUseOptions as IOptions[]}
								error={formik.touched.vehicleUse && Boolean(formik.errors.vehicleUse)}
								helperText={formik.touched.vehicleUse && formik.errors.vehicleUse}
								modal={{
									title: VEHICLE_USED_FOR.header,
									description: VEHICLE_USED_FOR.text,
								}}
								product={fullPolicy.product}
							/>
						) : (
							<ToggleButtonFormField
								label='What will you be using the vehicle for?'
								name='vehicleUse'
								id='vehicleUse'
								value={formik.values.vehicleUse}
								onChange={(value) => {
									formik.setFieldValue('vehicleUse', value);
								}}
								options={taxiUseOptionsFilter}
								error={formik.touched.vehicleUse && Boolean(formik.errors.vehicleUse)}
								helperText={formik.touched.vehicleUse && formik.errors.vehicleUse}
								modal={{
									title: TAXI_VEHICLE_USE.header,
									description: TAXI_VEHICLE_USE.text,
								}}
								product={fullPolicy.product}
							/>
						)}

						<AutoCompleteFormField
							label='Please select your licensing authority'
							name='licencingAuthority'
							id='licencingAuthority'
							value={formik.values.licencingAuthority}
							onChange={(value) => formik.setFieldValue('licencingAuthority', value)}
							options={lists.licencingArea as IOptions[]}
							error={formik.touched.licencingAuthority && Boolean(formik.errors.licencingAuthority)}
							helperText={formik.touched.licencingAuthority && formik.errors.licencingAuthority}
						/>
						<AutoCompleteFormField
							label='Primary operating area'
							name='operatingArea'
							id='operatingArea'
							value={formik.values.operatingArea}
							onChange={(value) => formik.setFieldValue('operatingArea', value)}
							options={lists.licencingArea as IOptions[]}
							error={formik.touched.operatingArea && Boolean(formik.errors.operatingArea)}
							helperText={formik.touched.operatingArea && formik.errors.operatingArea}
						/>
						{activePolicyDetails.vehicleDetails.coverDefinitionCode !== 'TPO' && (
							<SelectFormField
								label='What level of cover do you require?'
								name='levelOfCover'
								id='levelOfCover'
								value={formik.values.levelOfCover}
								onChange={(value) => formik.setFieldValue('levelOfCover', value)}
								options={coverOptions}
								error={formik.touched.levelOfCover && Boolean(formik.errors.levelOfCover)}
								helperText={formik.touched.levelOfCover && formik.errors.levelOfCover}
								modal={{
									title: COVER_LEVEL_UPDATED(fullPolicy.startDate, fullPolicy.product).header,
									description: COVER_LEVEL_UPDATED(fullPolicy.startDate, fullPolicy.product).text,
								}}
							/>
						)}
						<ActionButtons>
							<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
							<ContinueButton type='submit'>Continue</ContinueButton>
						</ActionButtons>
						<HelperModal
							open={open}
							onClose={() => {
								toggleModal(false);
							}}
							title='Discard The Changes?'
							description='Are you sure you wish to discard the changes?'
							closeText='No'
							submitText='Yes'
							onSubmit={() => {
								formik.resetForm();
							}}
						/>
					</Form>
				)}
			</Formik>
		</JourneyCardLayout>
	);
};

export default ChangeOfUsageTaxi;
